"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import type { CSSProperties } from "react";
import { useEffect, useState } from "react";

import {
  ClosingSection,
  DATA_LABEL_CLS,
  DISPLAY,
  EditorialShell,
  EDITORIAL_CONTAINER_CLS,
  EDITORIAL_SECTION_CLS,
  FaqSection,
  GROUND,
  Grain,
  INK,
  LABEL_CLS,
  ListSection,
  ParagraphSection,
  SectionMarker,
  SECTION_INTRO_CLS,
  SECTION_TITLE_CLS,
  SURFACE_DARK,
  WAITLIST_HREF,
  type ListItem,
} from "@/components/editorial";
import { FAQS } from "@/lib/faqs";
import { Engraving } from "@/app/concepts/engraving";
import { CSS as BRAND_CSS } from "@/app/concepts/brand/_engravings";
import { F as BrandFigures } from "@/app/concepts/brand/_figures";
import {
  LiveEstates,
  ProofScope,
  ReconciliationPlate,
} from "./editorial/proof-plates";
import { EvidenceRiser } from "./editorial/glass-twin/evidence-riser";
import twinStyles from "./editorial/glass-twin/glass-twin.module.css";
import {
  getTwinSequenceTime,
  TWIN_SEQUENCE,
} from "./editorial/glass-twin/sequence";

const GlassTwin3D = dynamic(
  () =>
    import("@/components/editorial/glass-twin/glass-twin-3d").then(
      (module) => module.GlassTwin3D,
    ),
  { ssr: false },
);

const GlassTwinIngress = dynamic(
  () =>
    import("@/components/editorial/glass-twin/glass-twin-3d").then(
      (module) => module.GlassTwinIngress,
    ),
  { ssr: false },
);

const TWIN_PROCESS_STEPS = [
  {
    startsAt: 0,
    text: "01 · Approved sources connect",
  },
  {
    startsAt: TWIN_SEQUENCE.ingestSeconds,
    text: "02 · Relevant evidence gathers",
  },
  {
    startsAt: TWIN_SEQUENCE.ingestSeconds + 6,
    text: "03 · Business context is proposed",
  },
  {
    startsAt: TWIN_SEQUENCE.ingestSeconds + 11,
    text: "04 · Your team reviews and approves",
  },
] as const;

const HOME_TOKENS = {
  "--rule": "color-mix(in srgb, var(--theme-ink) 22%, transparent)",
  "--rule-soft": "color-mix(in srgb, var(--theme-ink) 7%, transparent)",
  "--type-label": "0.6875rem",
  "--type-label-small": "0.6875rem",
  "--type-data": "0.75rem",
} as CSSProperties;

const ACTION_CLASS =
  "inline-flex min-h-11 items-center justify-center px-[1.2rem] py-[0.7rem] text-[0.9rem] font-semibold transition-[transform,background-color] duration-150 ease-out active:scale-[0.97] motion-reduce:transition-none";

const DIAL_TICKS = Array.from({ length: 96 }, (_, index) => ({
  angle: index * 3.75,
  major: index % 12 === 0,
}));

const TITLE_BLOCK = [
  ["Built from", "Your systems, terms, rules and ways of working"],
  ["Evidence", "Approved sources stay authoritative"],
  ["Approved by", "Your team, before AI relies on it"],
  ["Used by", "The AI tools and workflows you approve"],
  [
    "Model choice",
    "Use the best configured model for each task · switch without rebuilding the context",
  ],
  ["Operated", "Managed and governed in MLX"],
] as const;

/**
 * Six evidence families align with the EvidenceRiser's six vertical datums.
 * This is the approved marketing connector set, kept in lockstep with the FAQ
 * ("Which systems can you connect?"). The footer row carries the FAQ's scoping
 * promise instead of padding the grid with unbuilt connectors.
 */
const EVIDENCE_FAMILIES = [
  {
    label: "Accounting · payments",
    sources: [
      { name: "Xero", icon: "xero.svg" },
      { name: "SunSystems", icon: "sunsystems.svg" },
      { name: "Stripe", icon: "stripe.svg" },
      { name: "GoCardless", icon: "gocardless.svg" },
    ],
  },
  {
    label: "Documents · files",
    sources: [
      { name: "Microsoft 365", icon: "microsoft365.svg" },
      { name: "SharePoint", icon: "sharepoint.svg" },
      { name: "Google Drive", icon: "googledrive.svg" },
    ],
  },
  {
    label: "Docs · knowledge",
    sources: [
      { name: "Google Docs", icon: "googledocs.svg" },
      { name: "Google Sheets", icon: "googlesheets.svg" },
      { name: "Notion", icon: "notion.svg" },
    ],
  },
  {
    label: "Email · calendar",
    sources: [
      { name: "Gmail", icon: "gmail.png" },
      { name: "Calendar", icon: "googlecalendar.png" },
      { name: "Outlook", icon: "outlook.svg" },
    ],
  },
  {
    label: "Chat · meetings",
    sources: [
      { name: "Slack", icon: "slack.png" },
      { name: "Teams", icon: "teams.svg" },
      { name: "Granola", icon: "granola.png" },
    ],
  },
  {
    label: "Engineering · projects",
    sources: [
      { name: "GitHub", icon: "github.png" },
      { name: "Linear", icon: "linear.svg" },
    ],
  },
] as const;

const TWIN_STATES = [
  {
    number: "01",
    title: "Connect source evidence",
    body: "Scoped, read-only connectors bring relevant evidence from approved source systems into the Twin while those systems remain authoritative.",
  },
  {
    number: "02",
    title: "Prepare business context",
    body: "MLX proposes definitions and calculations, links them to their sources and records each approved version.",
  },
  {
    number: "03",
    title: "Review, approve and publish",
    body: "Your people review the proposals. What they approve becomes durable business context that approved AI can use.",
  },
] as const;

const BENEFITS: ListItem[] = [
  {
    n: "01",
    title: "One trusted business context",
    body: "Approved AI tools work from the same versioned understanding of your business instead of rebuilding it in every chat and workflow.",
  },
  {
    n: "02",
    title: "Use the data you already have",
    body: "Make modern and legacy data usable by AI without replacing the systems that hold it. Those systems remain authoritative.",
  },
  {
    n: "03",
    title: "Answers show what they used",
    body: "Each governed answer identifies the approved definition and version it used, with governed evidence references. Configured products can also expose source freshness.",
  },
  {
    n: "04",
    title: "Choose models and control cost",
    body: "Use stronger models for demanding work, lower-cost models for routine tasks or change providers without rebuilding the Twin's business context.",
  },
];

/**
 * The proof band: each claim paired with its certified artefact, drawn as
 * the document a controller would expect to receive. Plate figures are
 * representative renderings; the measured claims live in the body copy.
 */
const PROOF_EXHIBITS = [
  {
    n: "01",
    marker: "Accurate and explainable",
    title: "Results your team can verify.",
    body: "The Twin produced a P&L from reviewed business definitions and calculations. When finance checked it line by line against its approved ground truth, all nine lines matched, with £0.00 variance. The definitions, calculation version and supporting evidence remain traceable.",
    note: "Representative figures protect confidentiality · measured result: 9 of 9 lines matched",
    Plate: ReconciliationPlate,
  },
  {
    n: "02",
    marker: "Modern and legacy systems",
    title: "Works with the systems you already run.",
    body: "MLX is already working with data from Xero and a legacy SunSystems ledger. Both systems remain authoritative while the Twin turns their data into reviewed metrics and processes—without a wholesale migration.",
    note: "Real operating environments · read-only connections · reviewed by the team",
    Plate: LiveEstates,
  },
] as const;

const ADOPTION: ListItem[] = [
  {
    n: "01",
    title: "Connect priority systems",
    body: "Connect the systems that matter first, starting read-only. MLX identifies the evidence they can support.",
    extra: "Days 1–5",
  },
  {
    n: "02",
    title: "Review what MLX proposes",
    body: "Your experts inspect the proposed definitions, relationships and measures, resolve exceptions and approve what is reliable.",
    extra: "Days 6–10",
  },
  {
    n: "03",
    title: "Put approved context to work",
    body: "Publish the approved business context and use it through the AI tools configured for your deployment.",
    extra: "End of week 2",
  },
];

/**
 * Worked example for the dark section: the same question answered by a
 * generic copilot and by a governed Twin query. Figures are illustrative
 * and labelled as such; the plain-language metadata rows mirror the evidence
 * and unsupported-question handling a governed query actually returns.
 */
const SPECIMEN_QUESTION = "“What was revenue last quarter?”";

const SPECIMEN_COLUMNS = [
  {
    title: "A generic copilot",
    answer: "“Revenue was around £1.2m, based on the documents I could reach.”",
    emphasis: false,
    rows: [
      ["Source", "Whatever it could reach"],
      ["Version", "None"],
      ["Freshness", "Unknown"],
      ["Unsupported", "Answers anyway"],
    ],
  },
  {
    title: "An approved Twin answer",
    answer: "£1,243,908 · Apr–Jun 2026",
    emphasis: true,
    rows: [
      ["Approved definition", "Monthly revenue · v2"],
      ["Data current to", "30 Jun 2026"],
      ["Evidence used", "Published business view"],
      ["If unsupported", "Says so · never guesses"],
    ],
  },
] as const;

function GovernedAnswerSpecimen() {
  return (
    <div className="mt-14 md:mt-16">
      <div className="border border-[var(--rule)]">
        <div className="flex min-h-[2.65rem] items-center justify-between gap-4 border-b border-dotted border-[var(--rule)] px-4 py-[0.7rem] max-md:flex-col max-md:items-start max-md:gap-1">
          <span className={`text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)] ${DATA_LABEL_CLS}`}>
            Exhibit — the governed result, as an agent receives it
          </span>
          <strong className={`text-[var(--theme-accent)] ${DATA_LABEL_CLS}`}>
            Illustrative specimen
          </strong>
        </div>
        <div className="border-b border-dotted border-[var(--rule)] px-4 py-4">
          <p className={`text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)] ${DATA_LABEL_CLS}`}>Question</p>
          <p className="mt-2 text-xl font-semibold tracking-[-0.01em]" style={DISPLAY}>{SPECIMEN_QUESTION}</p>
        </div>
        {/* Subgrid keeps the metadata rows of both halves on shared tracks so
            the dotted rules align even when a value wraps; browsers without
            subgrid fall back to per-column row heights. */}
        <div className="grid grid-cols-2 grid-rows-[repeat(6,auto)] max-md:grid-cols-1 max-md:grid-rows-none">
          {SPECIMEN_COLUMNS.map((column, index) => (
            <div
              className={`row-span-6 grid grid-rows-subgrid max-md:row-auto max-md:grid-rows-none ${
                index === 0
                  ? "border-r border-dotted border-[var(--rule)] max-md:border-r-0 max-md:border-b"
                  : ""
              }`}
              key={column.title}
            >
              <p className={`border-b border-dotted border-[var(--rule)] px-5 py-3 text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)] ${DATA_LABEL_CLS}`}>
                {column.title}
              </p>
              <p
                className={`min-h-[4.6rem] px-5 py-4 text-[1.05rem] leading-[1.5] ${
                  column.emphasis
                    ? "font-semibold text-[var(--theme-ground)]"
                    : "text-[color-mix(in_srgb,var(--theme-ground)_88%,transparent)]"
                }`}
                style={DISPLAY}
              >
                {column.answer}
              </p>
              {column.rows.map(([label, value]) => (
                <div
                  className="grid min-h-8 grid-cols-[minmax(9.5rem,36%)_minmax(0,1fr)] border-t border-dotted border-[var(--rule)]"
                  key={label}
                >
                  <span className="flex items-center border-r border-dotted border-[var(--rule)] px-3 py-2 text-[length:var(--type-label-small)] font-medium leading-tight tracking-[0.16em] text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)] uppercase">
                    {label}
                  </span>
                  <span
                    className={`flex items-center px-4 py-2 text-[length:var(--type-data)] font-medium tracking-[0.04em] uppercase ${
                      column.emphasis
                        ? "text-[color-mix(in_srgb,var(--theme-ground)_92%,transparent)]"
                        : "text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)]"
                    }`}
                  >
                    {value}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PreparedResultsNote() {
  return (
    <div className="mt-14 grid grid-cols-[0.8fr_1.2fr] gap-12 border-y border-[var(--rule)] py-10 max-lg:grid-cols-1 max-lg:gap-6 md:mt-16">
      <div>
        <p className={`text-[var(--theme-accent)] ${DATA_LABEL_CLS}`}>
          Prepared business logic
        </p>
        <h3
          className="mt-3 max-w-[16ch] text-[clamp(1.8rem,3vw,2.7rem)] leading-[1.05] font-semibold tracking-[-0.03em] text-[var(--theme-ground)]"
          style={DISPLAY}
        >
          Fast answers from approved calculations.
        </h3>
      </div>
      <div>
        <p className="max-w-[42rem] text-[1.05rem] leading-[1.65] text-[color-mix(in_srgb,var(--theme-ground)_90%,transparent)]">
          MLX prepares approved business calculations before AI needs them.
          That produces significantly faster answers, consistent numbers and
          lower model costs.
        </p>
        <p className="mt-4 max-w-[42rem] text-[0.98rem] leading-[1.65] text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)]">
          When the approved calculation and source data have not changed, the
          result does not change either—and its evidence stays attached.
        </p>
      </div>
    </div>
  );
}

function LegacyDataBridge() {
  return (
    <section
      aria-labelledby="legacy-data-heading"
      className="relative overflow-hidden border-t border-[var(--rule)]"
      style={{ backgroundColor: GROUND, color: INK }}
    >
      <Grain opacity={0.35} />
      <div className={`relative z-[2] py-16 md:py-20 ${EDITORIAL_CONTAINER_CLS}`}>
        <p className={`text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}>
          Modern and legacy systems
        </p>
        <div className="mt-6 grid grid-cols-[1.05fr_0.95fr] gap-12 max-lg:grid-cols-1 max-lg:gap-8">
          <h2
            id="legacy-data-heading"
            className={`max-w-[16ch] ${SECTION_TITLE_CLS}`}
            style={DISPLAY}
          >
            Make legacy data usable by AI—without replacing the systems that
            hold it.
          </h2>
          <div className="max-w-[42rem]">
            <p className="text-[1.05rem] leading-[1.65] text-[color-mix(in_srgb,var(--theme-ink)_90%,transparent)]">
              MLX connects the priority sources you approve and turns their
              records into reviewed business context AI can use. Your existing
              systems remain authoritative, so you can start with legacy
              applications, specialist databases, spreadsheets and documents
              without a wholesale migration.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProofSection() {
  return (
    <section className="relative overflow-hidden" style={{ color: INK }}>
      <ProofScope>
        <div className={`relative z-[2] ${EDITORIAL_SECTION_CLS}`}>
          <SectionMarker section="04" label="Proof from real systems" />
          <div className="mt-10 max-w-3xl">
            <h2 className={SECTION_TITLE_CLS} style={DISPLAY}>
              From existing systems to trusted results.
            </h2>
            <p className={`mt-6 text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)] ${SECTION_INTRO_CLS}`}>
              MLX connects to the systems you already run, reconstructs the
              definitions and calculations behind your numbers, and gives your
              team results they can inspect, correct and approve. These
              examples come from real modern and legacy finance environments.
            </p>
          </div>

          {/* Each exhibit reuses the numbered-list anatomy from ListSection
              (3rem numeral rail + content) so the proof band reads as part of
              the same system; the plate then spans the full column. */}
          {PROOF_EXHIBITS.map(({ n, marker, title, body, note, Plate }) => (
            <div
              className="mt-14 grid grid-cols-[3rem_1fr] items-start gap-x-4 border-t border-[color-mix(in_srgb,var(--theme-ink)_18%,transparent)] pt-5 md:mt-16"
              key={n}
            >
              <span
                className="text-[1.3rem] leading-none font-semibold tracking-[-0.01em] tabular-nums text-[var(--theme-accent-text)]"
                style={DISPLAY}
              >
                {n}
              </span>
              <div className="min-w-0">
                <p className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}>
                  {marker}
                </p>
                <h3
                  className="mt-2 text-[1.15rem] leading-[1.3] font-semibold tracking-[-0.01em] md:text-[1.25rem]"
                  style={DISPLAY}
                >
                  {title}
                </h3>
                <p className="mt-2 max-w-[44rem] text-[0.98rem] leading-[1.6] text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]">
                  {body}
                </p>
              </div>
              {/* The concept stylesheet gives plates their own uneven top
                  margins (26px plate/panel, 6px triptych/estates); zero them
                  so this wrapper's mt-8 is the one heading-to-plate gap. */}
              <div className="col-span-2 md:col-span-1 md:col-start-2 mt-8 min-w-0 [&_.estates]:!mt-0 [&_.panel]:!mt-0 [&_.plate]:!mt-0 [&_.triptych]:!mt-0">
                <Plate />
                {note ? (
                  <p className={`mt-4 text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}>
                    {note}
                  </p>
                ) : null}
              </div>
            </div>
          ))}

          <div className="mt-14 grid grid-cols-[3rem_1fr] items-start gap-x-4 border-t border-[color-mix(in_srgb,var(--theme-ink)_18%,transparent)] pt-5 md:mt-16">
            <span
              className="text-[1.3rem] leading-none font-semibold tracking-[-0.01em] tabular-nums text-[var(--theme-accent-text)]"
              style={DISPLAY}
            >
              03
            </span>
            <div className="min-w-0">
              <p className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}>
                A focused first result
              </p>
              <h3
                className="mt-2 text-[1.15rem] leading-[1.3] font-semibold tracking-[-0.01em] md:text-[1.25rem]"
                style={DISPLAY}
              >
                Start with something useful—not a replacement programme.
              </h3>
              <p className="mt-2 max-w-[44rem] text-[0.98rem] leading-[1.6] text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]">
                A focused two-week activation starts with one priority workflow
                and the systems behind it. Your team reviews the first business
                context before deciding where the Twin should expand next.
              </p>
            </div>
          </div>
        </div>
      </ProofScope>
    </section>
  );
}

const AI_SURFACES = [
  { name: "MLX", src: "/logo.svg" },
  { name: "ChatGPT", src: "/concepts/source-icons/chatgpt.svg" },
  { name: "Claude", src: "/concepts/source-icons/claude.svg" },
  { name: "Gemini", src: "/concepts/source-icons/gemini.svg" },
] as const;

const STACK_EXTRA_CSS = `
.concepts-brand.stack-exhibit-dark{
  --dark:var(--theme-surface-dark);
  --ground:var(--theme-surface-dark);
  --ink:var(--theme-ground);
  --dot:color-mix(in srgb,var(--theme-accent) 8%,transparent);
  background-color:var(--theme-surface-dark);
  background-image:radial-gradient(var(--dot) 1px,transparent 1.6px);
  color:var(--theme-accent);
}
.concepts-brand.stack-exhibit-dark .stack-brd006-figure svg{
  display:block;
  width:100%;
  height:auto;
}
`;

/**
 * Section 03 exhibit — the exact BRD-006 engraving, followed by a compact set
 * of representative AI tools that can use the same governed Twin.
 */
function StackExhibit() {
  return (
    <div className="mt-14 md:mt-16">
      <div className="concepts-brand stack-exhibit-dark relative overflow-hidden rounded-[3px] border border-[color-mix(in_srgb,var(--theme-accent)_24%,transparent)]">
        <style>{BRAND_CSS + STACK_EXTRA_CSS}</style>
        <div
          aria-hidden="true"
          className="h-1 w-full bg-[var(--theme-accent)]"
        />
        <div className="flex min-h-[3.25rem] items-center justify-between gap-4 border-b border-dotted border-[color-mix(in_srgb,var(--theme-accent)_28%,transparent)] px-5 py-3.5 md:px-7 max-md:flex-col max-md:items-start max-md:gap-1.5">
          <span className={`text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)] ${DATA_LABEL_CLS}`}>
            Exhibit — one Twin, approved AI
          </span>
          <strong className={`text-[var(--theme-accent)] ${DATA_LABEL_CLS}`}>
            Keep the map · choose the intelligence
          </strong>
        </div>

        <div className="px-5 pt-10 md:px-10 md:pt-14">
          <div className="stack-brd006-figure mx-auto w-full max-w-[52rem]">
            <Engraving data={BrandFigures.darkPanel} />
          </div>

          <div
            aria-hidden="true"
            className="mx-auto flex h-20 flex-col items-center"
          >
            <div className="h-12 border-l border-[color-mix(in_srgb,var(--theme-accent)_55%,transparent)]" />
            <div className="h-2.5 w-2.5 rotate-45 border-r border-b border-[color-mix(in_srgb,var(--theme-accent)_70%,transparent)]" />
          </div>

          <div className={`mx-auto mb-5 w-full max-w-[52rem] text-left text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)] ${DATA_LABEL_CLS}`}>
            Approved context · available through configured AI tools
          </div>

          <ul
            aria-label="AI providers and interfaces that can use the Twin"
            className="mx-auto grid w-full max-w-[52rem] grid-cols-4 gap-px bg-[color-mix(in_srgb,var(--theme-accent)_22%,transparent)] p-px max-md:grid-cols-2"
          >
            {AI_SURFACES.map((surface) => (
              <li
                key={surface.name}
                className="flex min-h-[5.75rem] flex-col items-center justify-center gap-3 bg-[var(--theme-surface-dark)] px-3 py-4 text-center"
              >
                <Image
                  src={surface.src}
                  alt=""
                  width={34}
                  height={34}
                  className="h-[2.125rem] w-[2.125rem] object-contain"
                />
                <span className={`text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)] ${DATA_LABEL_CLS}`}>
                  {surface.name}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-12 border-t border-dotted border-[color-mix(in_srgb,var(--theme-accent)_28%,transparent)] px-5 py-10 md:px-10 md:py-12">
          <div className="mx-auto max-w-[52rem]">
            <p className={`text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)] ${DATA_LABEL_CLS}`}>
              One governed Twin · approved AI
            </p>
            <h3
              className="mt-4 max-w-[24ch] text-[2rem] leading-[1.08] font-semibold tracking-[-0.025em] text-[var(--theme-accent)] md:text-[2.5rem]"
              style={DISPLAY}
            >
              One Twin. The right AI for each job.
            </h3>
            <p className="mt-6 max-w-[58rem] text-[1.05rem] leading-[1.7] text-[color-mix(in_srgb,var(--theme-ground)_88%,transparent)] md:text-[1.12rem]">
              Use the same approved business context through MLX and the AI
              tools configured for your deployment. Each one works from the
              same reviewed understanding of how your business operates.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function RegistrationFrame() {
  return (
    <div className={twinStyles.registrationFrame} aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
    </div>
  );
}

function InstrumentDial() {
  return (
    <svg
      className={twinStyles.dial}
      viewBox="0 0 560 560"
      role="presentation"
      aria-hidden="true"
    >
      <circle cx="280" cy="280" r="258" />
      <circle cx="280" cy="280" r="232" />
      {DIAL_TICKS.map(({ angle, major }) => {
        return (
          <line
            key={angle}
            x1="280"
            y1={major ? 22 : 29}
            x2="280"
            y2={major ? 48 : 40}
            transform={`rotate(${angle} 280 280)`}
          />
        );
      })}
      <text x="280" y="14" textAnchor="middle">0</text>
      <text x="548" y="284" textAnchor="start">90</text>
      <text x="280" y="557" textAnchor="middle">180</text>
      <text x="12" y="284" textAnchor="end">270</text>
    </svg>
  );
}

function TwinProcessCaption() {
  const [activeStep, setActiveStep] = useState(0);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(media.matches);
    const handleChange = (event: MediaQueryListEvent) => {
      setReduced(event.matches);
    };
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    if (reduced) {
      setActiveStep(TWIN_PROCESS_STEPS.length - 1);
      return;
    }
    let timeout = 0;

    const update = () => {
      const elapsed = getTwinSequenceTime();
      let nextStep = 0;
      for (let index = 1; index < TWIN_PROCESS_STEPS.length; index += 1) {
        const step = TWIN_PROCESS_STEPS[index];
        if (!step || elapsed < step.startsAt) break;
        nextStep = index;
      }
      setActiveStep(nextStep);

      const followingStep = TWIN_PROCESS_STEPS[nextStep + 1];
      const secondsUntilChange = followingStep
        ? followingStep.startsAt - elapsed
        : TWIN_SEQUENCE.totalSeconds - elapsed;
      timeout = window.setTimeout(
        update,
        Math.max(50, secondsUntilChange * 1000),
      );
    };

    update();
    return () => window.clearTimeout(timeout);
  }, [reduced]);

  return (
    <p
      role="status"
      aria-live="polite"
      className="absolute inset-x-0 top-[calc(100%+4.5rem)] min-h-4 text-center text-[length:var(--type-label-small)] font-medium tracking-[0.18em] text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] uppercase max-md:hidden lg:max-[1200px]:text-[0.65rem] lg:max-[1200px]:tracking-[0.06em]"
    >
      <span className="sr-only">
        Twin process: approved sources connect, relevant evidence gathers,
        business context is proposed, and your team reviews and approves it.
      </span>
      <span
        key={activeStep}
        className={`${twinStyles.instrumentNoteStep} whitespace-nowrap`}
        aria-hidden="true"
      >
        {TWIN_PROCESS_STEPS[activeStep]?.text}
      </span>
    </p>
  );
}

function TwinHero() {
  return (
    <section
      id="top"
      data-twin-stage
      className="relative flex min-h-[max(100svh,1120px)] flex-col overflow-hidden max-lg:min-h-0"
      style={{ backgroundColor: GROUND, color: INK }}
    >
      <Grain opacity={0.3} />
      <RegistrationFrame />
      <div
        className={`${twinStyles.particleStage} gl3d-stage pointer-events-none absolute inset-0 z-[1]`}
        aria-hidden="true"
      >
        <GlassTwinIngress particleScale={1.9} />
      </div>

      <div className={`relative z-[4] pt-44 pb-20 lg:min-h-[calc(13.8rem+min(44vw,37rem)+10rem)] max-lg:pt-28 max-lg:pb-0 max-md:pt-24 ${EDITORIAL_CONTAINER_CLS}`}>
        <div className="w-[min(46%,38rem)] max-lg:mx-auto max-lg:w-auto max-lg:max-w-[46rem] max-lg:px-10 max-lg:text-center max-md:px-3">
          <div className="flex items-center gap-3 text-[var(--theme-accent-text)] max-lg:justify-center">
            <span className="size-2 bg-[var(--theme-dark)]" aria-hidden="true" />
            <p className={LABEL_CLS}>Drawing 00 — The AI Twin</p>
          </div>
          <h1 className="mt-[2.1rem] max-w-[10ch] text-[clamp(3.6rem,5.7vw,6.2rem)] leading-[0.95] font-semibold tracking-[-0.04em] max-lg:mx-auto max-lg:max-w-[12ch] max-md:text-[clamp(3.1rem,14vw,4.5rem)]" style={{ ...DISPLAY, color: INK }}>
            Your business, twinned for AI.
          </h1>
          <p className="mt-[1.8rem] max-w-[34rem] text-[clamp(1rem,1.45vw,1.22rem)] leading-normal font-medium tracking-[-0.01em] text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)] max-lg:mx-auto" style={DISPLAY}>
            Generic AI doesn&apos;t know your business. MLX constructs a
            governed Twin from the systems you already run—reviewed by your
            team and usable by the AI you approve.
          </p>

          <div className="mt-5 flex flex-wrap gap-[0.6rem] max-lg:justify-center">
            <a className={`${ACTION_CLASS} bg-[var(--theme-surface-dark)] text-[var(--theme-ground)] hover:bg-[color-mix(in_srgb,var(--theme-surface-dark)_88%,var(--theme-ink))]`} href={WAITLIST_HREF}>Discuss your first activation</a>
            <a className={`${ACTION_CLASS} border border-[var(--theme-dark)] text-[var(--theme-accent-text)] hover:bg-[color-mix(in_srgb,var(--theme-dark)_7%,transparent)]`} href="#how-it-works">See how it works</a>
          </div>
        </div>
      </div>

      <div className={`relative z-[3] mt-20 mb-28 lg:pointer-events-none lg:absolute lg:inset-x-0 lg:top-0 lg:mt-0 lg:h-full lg:mb-0 max-md:mt-16 max-md:mb-20 ${EDITORIAL_CONTAINER_CLS}`}>
        <div className={`${twinStyles.particleStage} relative mx-auto aspect-square w-[min(70vw,32rem)] lg:absolute lg:top-[13.8rem] lg:right-14 lg:w-[min(44vw,37rem)] max-md:w-[82vw]`}>
          <div className="absolute -top-[2.8rem] left-[11%] flex items-center gap-3 text-[var(--theme-accent-text)] max-lg:left-1/2 max-lg:-translate-x-1/2 max-lg:justify-center max-lg:whitespace-nowrap max-md:-top-[2.2rem]">
            <span className="size-2 shrink-0 bg-[var(--theme-dark)]" aria-hidden="true" />
            <p className={LABEL_CLS}>Front elevation — The AI Twin</p>
          </div>
          <InstrumentDial />
          <div className="gl3d-hole">
            <GlassTwin3D particleScale={2.25} />
          </div>
          <div
            className={`${twinStyles.dimension} absolute right-[15%] -bottom-14 left-[15%] h-[2.2rem] border-t border-[var(--rule)]`}
            aria-hidden="true"
          />
          <TwinProcessCaption />
        </div>
      </div>

      <div className={`relative z-[4] mt-24 mb-10 max-lg:mt-12 max-md:mt-8 max-md:mb-6 ${EDITORIAL_CONTAINER_CLS}`}>
        <div className="relative border border-[var(--rule)] bg-[color-mix(in_srgb,var(--theme-ground)_86%,transparent)]">
          <EvidenceRiser />
          <div className="flex min-h-[2.65rem] items-center justify-between gap-4 border-b border-dotted border-[var(--rule)] px-4 py-[0.7rem] max-md:flex-col max-md:items-start max-md:gap-1">
            <span className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}>Connected evidence sources</span>
            <strong className={`text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}>Your source systems remain authoritative</strong>
          </div>
          <div className="grid grid-cols-6 gap-px bg-[var(--rule-soft)] max-lg:grid-cols-3 max-md:grid-cols-2">
            {EVIDENCE_FAMILIES.map((family) => (
              <div className="min-w-0 bg-[color-mix(in_srgb,var(--theme-ground)_94%,#fff)] px-4 py-3 max-md:p-3" key={family.label}>
                {/* Fixed two-line label zone so the icon rows start at the
                    same height in every column, wrapped or not. */}
                <span className={`block min-h-[2.1rem] text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}>{family.label}</span>
                <div className="mt-2 flex min-h-[8rem] flex-col justify-start gap-2">
                  {family.sources.map((source) => (
                    <div className="flex min-w-0 items-center gap-2" key={source.name}>
                      <Image
                        src={`/concepts/source-icons/${source.icon}`}
                        width={26}
                        height={26}
                        alt=""
                        loading="lazy"
                        className="size-[26px] shrink-0 object-contain"
                      />
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[0.6rem] font-semibold tracking-[0.1em] text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] uppercase">{source.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex min-h-[2.2rem] items-center gap-2 border-t border-dotted border-[var(--rule)] px-4 py-[0.55rem]">
            <span
              className="flex size-[18px] shrink-0 items-center justify-center border border-dashed border-[var(--rule)] text-[0.65rem] font-semibold text-[color-mix(in_srgb,var(--theme-ink)_66%,transparent)]"
              aria-hidden="true"
            >
              +
            </span>
            <span className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}>Further sources scoped per activation</span>
          </div>
        </div>
      </div>

      <div className={`relative z-[4] mb-10 max-md:mb-6 ${EDITORIAL_CONTAINER_CLS}`}>
        <dl
          className="border border-[var(--rule)] bg-[color-mix(in_srgb,var(--theme-ground)_92%,transparent)]"
        >
          <div className="grid min-h-8 grid-cols-[minmax(12rem,22%)_minmax(0,1fr)] border-b border-dotted border-[var(--rule)] max-md:grid-cols-1">
            <dt className="flex items-center border-r border-dotted border-[var(--rule)] px-3 py-2 text-[length:var(--type-label)] font-medium tracking-[0.15em] text-[var(--theme-ink)] uppercase max-md:border-r-0 max-md:border-b">MLX</dt>
            <dd className="m-0 flex items-center px-3 py-2 text-[length:var(--type-label)] font-medium tracking-[0.15em] text-[var(--theme-ink)] uppercase">One trusted view of your business</dd>
          </div>
          {TITLE_BLOCK.map(([label, value]) => (
            <div className="grid min-h-8 grid-cols-[minmax(12rem,22%)_minmax(0,1fr)] border-b border-dotted border-[var(--rule)] last:border-b-0 max-md:grid-cols-[10rem_minmax(0,1fr)]" key={label}>
              <dt className="flex items-center border-r border-dotted border-[var(--rule)] px-3 py-2 text-[length:var(--type-label-small)] font-medium tracking-[0.16em] text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] uppercase max-md:tracking-[0.12em]">{label}</dt>
              <dd className="m-0 flex items-center px-3 py-2 text-[length:var(--type-data)] font-medium tracking-[0.04em] uppercase">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function TwinStates() {
  return (
    <section
      id="how-it-works"
      className="relative scroll-mt-16 overflow-hidden [--rule:color-mix(in_srgb,var(--theme-ground)_22%,transparent)] [--rule-soft:color-mix(in_srgb,var(--theme-ground)_6%,transparent)]"
      style={{ backgroundColor: SURFACE_DARK, color: GROUND }}
    >
      <Grain opacity={0.38} />
      <div className={`relative z-[2] pt-28 pb-32 max-md:pt-[5.5rem] max-md:pb-24 ${EDITORIAL_CONTAINER_CLS}`}>
        <SectionMarker section="02" label="How the Twin forms" dark />
        <div className="mt-[2.8rem] grid grid-cols-[1.25fr_0.75fr] gap-12 max-lg:grid-cols-1">
          <h2 className={`max-w-[15ch] text-[var(--theme-ground)] ${SECTION_TITLE_CLS}`} style={DISPLAY}>One business. Three states you can inspect.</h2>
          <p className={`max-w-[35rem] text-[color-mix(in_srgb,currentColor_88%,transparent)] ${SECTION_INTRO_CLS}`}>
            You can see what the Twin read, what it derived and what your team
            approved. Source evidence, transformation and the governed result
            remain distinct and inspectable.
          </p>
        </div>
        <ol className="mt-20 grid grid-cols-3 border-y border-[var(--rule)] max-lg:grid-cols-1">
          {TWIN_STATES.map((stage) => (
            <li className="min-h-80 border-r border-[var(--rule-soft)] px-8 pt-[1.7rem] pb-[2.2rem] first:pl-0 last:border-r-0 max-lg:min-h-0 max-lg:border-r-0 max-lg:border-b max-lg:border-[var(--rule)] max-lg:px-0 max-lg:pt-6 max-lg:pb-8 max-lg:last:border-b-0" key={stage.number}>
              <span className="text-[length:var(--type-label)] font-semibold tracking-[0.18em] text-[var(--theme-accent)]">{stage.number}</span>
              <h3 className="mt-20 text-2xl font-semibold tracking-[-0.02em] text-[var(--theme-ground)] max-lg:mt-[2.8rem]" style={DISPLAY}>{stage.title}</h3>
              <p className="mt-[0.9rem] leading-[1.55] text-[color-mix(in_srgb,var(--theme-ground)_88%,transparent)]">{stage.body}</p>
            </li>
          ))}
        </ol>
        <GovernedAnswerSpecimen />
        <PreparedResultsNote />
      </div>
    </section>
  );
}

export function EditorialHome() {
  return (
    <EditorialShell solidNav>
      {/* Plain <div>, not <main>: EditorialShell now provides the single
          <main> landmark, so this stays a styling wrapper (HOME_TOKENS). */}
      <div style={HOME_TOKENS}>
        <TwinHero />

        <ParagraphSection
          section="01"
          label="The missing context"
          numeral="01"
          title="AI has intelligence. It does not know your business."
          lead="Generic AI knows the world. It does not know what your accounts mean, how work moves, or which rules your team trusts. Every AI pilot rebuilds that context from scratch, and answers arrive unverifiable."
          body="An AI Twin closes that gap. It turns the evidence in your systems into reviewed, versioned business context the AI tools you approve can query."
          pullQuote="The model supplies intelligence. The Twin supplies your approved business context."
          pullQuoteLabel="Why now"
        />

        <LegacyDataBridge />

        <TwinStates />

        <ListSection
          id="platform"
          section="03"
          label="Why a Twin"
          numeral="03"
          title="One trusted business context. Many approved AI tools."
          intro="A governed Twin gives the AI you approve one shared, explainable view of how your company works. Your systems remain authoritative, and your people decide what becomes trusted context."
          items={BENEFITS}
          cols={2}
        >
          <StackExhibit />
        </ListSection>

        <ProofSection />

        <ListSection
          section="05"
          label="Activation"
          numeral="05"
          title="Start with one useful part of your business."
          intro="Connect priority systems, review what MLX constructs, then put the approved context to work in a focused two-week activation."
          items={ADOPTION}
          cols={3}
        />

        <FaqSection
          section="06"
          label="Buyer questions"
          numeral="06"
          title="What you control, and how it works."
          intro="Clear answers about data, model choice, deployment, cost and governance—grounded in the product available today."
          items={FAQS}
        />

        <ClosingSection
          content={{
            section: "07",
            label: "Your first activation",
            title: "Give your AI the business context it lacks.",
            body: "Choose a priority workflow, connect the systems behind it and review the first business context before approved AI puts it to work.",
            primaryCta: { label: "Discuss your first activation →", href: WAITLIST_HREF },
            secondaryCta: { label: "Mercury Labs", href: "https://mercurylabs.io" },
            meta: [
              { label: "Product", value: "The AI Twin" },
              { label: "Deploy", value: "Managed targets" },
              { label: "Built by", value: "Mercury Labs · London" },
            ],
          }}
        />
      </div>
    </EditorialShell>
  );
}
